import { useEffect, useState } from 'react';
import ThemeToggle from '../ThemeToggle';
import LanguageToggle from '../LanguageToggle';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import AuthService from '../../services/auth';
import { useAuthUser } from '../../services/authSync';
import { MeService } from '../../services/vendorDashboard';
import { VendorEntitlementsContext } from '../../context/VendorEntitlementsContext';
import { BRAND, BRAND_LOGO as logoNav } from 'config/brand';
import { t } from '../../i18n';

const navLinkClass = ({ isActive }) =>
  [
    'rounded-lg px-3 py-2 text-sm font-semibold transition',
    isActive ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
  ].join(' ');

// Sidebar built from GET /me/entitlements (CLAUDE.md D16 part B item 2) --
// hidden features don't get a link at all; read_only features still get a
// link (pages themselves hide the edit controls for those).
function buildLinks(features) {
  const links = [{ to: '/vendor', label: t('vendorNav.overview'), end: true }];
  if (features['products.manage'] !== 'hidden') {
    links.push({ to: '/vendor/products', label: t('vendorNav.products') });
  }
  if (features['inventory.manage'] !== 'hidden') {
    links.push({ to: '/vendor/inventory', label: t('vendorNav.inventory') });
  }
  if (features['orders.manage'] !== 'hidden') {
    links.push({ to: '/vendor/orders', label: t('vendorNav.orders') });
  }
  if (features['analytics.view'] !== 'hidden') {
    links.push({ to: '/vendor/analytics', label: t('vendorNav.analytics') });
  }
  if (features['store.settings'] !== 'hidden') {
    links.push({ to: '/vendor/store', label: t('vendorNav.storeLink') });
  }
  if (features['products.manage'] !== 'hidden') {
    links.push({ to: '/vendor/taxonomy-requests', label: t('vendorNav.taxonomy') });
  }
  if (features['module.fashion'] && features['module.fashion'] !== 'hidden') {
    links.push({ to: '/vendor/size-charts', label: t('modules.sizeCharts.nav') });
  }
  if (features['deals.countdown'] && features['deals.countdown'] !== 'hidden') {
    links.push({ to: '/vendor/deals', label: t('dealsAdmin.nav') });
  }
  if (features['reviews.reply'] && features['reviews.reply'] !== 'hidden') {
    links.push({ to: '/vendor/reviews', label: t('vendorReviews.nav') });
  }
  // Billing: documented exception (CLAUDE.md Code rules) -- a vendor must
  // always be able to view invoices and pay, regardless of entitlements.
  links.push({ to: '/vendor/billing', label: t('vendorNav.billing') });
  // Statement reuses the same billing.view read_only gate as Billing, so it
  // gets the same always-visible treatment.
  links.push({ to: '/vendor/statement', label: t('vendorNav.statement') });
  return links;
}

export default function VendorLayout({ children }) {
  const navigate = useNavigate();
  const user = useAuthUser(); // live across tabs (R0)
  const [menuOpen, setMenuOpen] = useState(false);
  const [entitlements, setEntitlements] = useState({ features: {}, limits: {}, loading: true });

  useEffect(() => {
    let cancelled = false;
    MeService.getEntitlements().then((result) => {
      if (cancelled) return;
      if (result.success) {
        setEntitlements({ features: result.features || {}, limits: result.limits || {}, loading: false });
      } else {
        setEntitlements({ features: {}, limits: {}, loading: false });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSignOut = () => AuthService.logout('/');

  return (
    <VendorEntitlementsContext.Provider value={entitlements}>
      <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white shadow-sm print:hidden">
        <div className="mx-auto max-w-[1400px] px-4 py-3 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <Link to="/vendor" className="inline-flex shrink-0 items-center">
                <img src={logoNav} alt={BRAND.name} className="h-9 w-auto object-contain" width={160} height={36} decoding="async" />
              </Link>
              <span className="hidden rounded-full bg-indigo-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-indigo-900 sm:inline">
                {t('vendorNav.dashboard')}
              </span>
            </div>

            <div className="flex items-center gap-3 text-sm">
              {user ? (
                <span className="hidden max-w-[200px] truncate font-medium text-slate-700 sm:inline">
                  {user.display_name || user.username}
                </span>
              ) : null}
              <button
                type="button"
                onClick={() => navigate('/')}
                className="hidden text-xs font-semibold text-slate-600 underline hover:text-slate-900 sm:inline"
              >
                {t('vendorNav.viewStore')}
              </button>
              <LanguageToggle className="h-8" />
              <ThemeToggle className="h-8 w-8" />
              <button
                type="button"
                onClick={handleSignOut}
                className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-800 transition hover:bg-slate-200"
              >
                {t('vendorNav.signOut')}
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 lg:hidden"
                onClick={() => setMenuOpen((o) => !o)}
                aria-expanded={menuOpen}
                aria-controls="vendor-nav-menu"
              >
                {t('nav.menu')}
              </button>
            </div>
          </div>

          <nav
            id="vendor-nav-menu"
            className={['mt-3 flex flex-wrap gap-1 border-t border-slate-100 pt-3', menuOpen ? 'flex' : 'hidden lg:flex'].join(' ')}
            aria-label={t('vendorNav.label')}
          >
            {buildLinks(entitlements.features).map((link) => (
              <NavLink key={link.to} to={link.to} end={link.end} className={navLinkClass} onClick={() => setMenuOpen(false)}>
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] px-4 py-8 sm:px-6">{children}</main>
    </VendorEntitlementsContext.Provider>
  );
}
